(ns app.front.view.group.admin.config.app-pc-selectors
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.section :as section]
            [app.front.view.group.admin.approval-authority-config :as approval-authority-config]
            [app.front.view.group.admin.case-id-config :as case-id-config]
            [app.front.view.group.admin.condition-due-date-config :as condition-due-date-config]
            [app.front.view.group.admin.config-selectors :as conf-sel]
            [app.front.view.group.admin.config.form-schema-subs :as cfs-subs]
            [app.front.view.group.admin.config.licence-notifications :as licence-notification-config]
            [app.front.view.group.admin.config.notification :as conf-notification]
            [app.front.view.group.admin.config.pre-con-expiration :as pre-con-expiration-config]
            [app.front.view.group.admin.config.role-form :as conf-role-form]
            [app.front.view.group.admin.config.sign-off :as conf-sof]
            [app.front.view.group.admin.config.verdict-defaults-config :as verdict-defaults]
            [app.front.view.group.admin.express-issuance-config :as express-issuance-config]
            [app.front.view.group.admin.inspections.template-selector :as inspection-sel]
            [app.front.view.group.admin.integration.ecm :as integration.ecm]
            [app.front.view.group.admin.integration.target-editor :as integration-editor]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.target-time-config :as target-time-config]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(defn is-attr? [attr {:keys [*group-config parent-config]}]
  (boolean (or (attr @*group-config)
               (->> parent-config attr (some val)))))

(defn- get-config-key [attr parent-config]
  (->> parent-config
       attr
       (filter val)
       ffirst))

(defn- make-toggle-id
  "Generate a toggle id without spaces"
  [attr name-chain]
  (str (name attr) "-" (str/join "-" (map (fn [s] (str/replace s #" " "-")) name-chain))))

(defn- on-change [attr *group-config]
  (swap! *group-config update attr not))

(defn toggle-selector
  [attr {:keys [tr parent-config *group-config name-chain] :as editor-ctx} & {:keys [disabled? disabled-title]}]
  (let [config-key     (get-config-key attr parent-config)
        disabled-title (or disabled-title
                           (when config-key
                             (tr [:group-admin/disabled-reason] [config-key])))]
    [:div.mb-4
     (cc/checkbox
       {:id         (make-toggle-id attr name-chain)
        :label-text (tr [attr])
        :on-change  #(on-change attr *group-config)
        :checked    (is-attr? attr editor-ctx)
        :disabled   (or disabled? (boolean config-key))
        :title      (or disabled-title "")})]))

(def execution-selector
  (partial toggle-selector :group.config/omit-execution-phase?))

(def no-review-change-request-selector
  (partial toggle-selector :group.config/no-review-change-requests))

(defn require-sign-off-after-changes-requested-selector [{:keys [tr *group-config parent-config] :as editor-ctx}]
  (let [sign-off-configured? (or (some? (:group.config/application-sign-off-schema @*group-config))
                                 (some? (:application-sign-off-schema parent-config)))]
    (toggle-selector :group.config/require-sign-off-after-changes-requested?
                     editor-ctx
                     (when-not sign-off-configured?
                       {:disabled?      true
                        :disabled-title (tr [:group-admin/require-sign-off-after-changes-disabled])}))))

(def no-workspace-location
  (partial toggle-selector :group.config/no-workspace-location))

(def hide-from-public-map
  (partial toggle-selector :group.config/hide-from-public-map))

(def public-map-visible-cr-selector
  (partial toggle-selector :group.config/public-map-visible-cr))

(defn public-map-visible-licence-selector [{:keys [tr] :as editor-ctx}]
  (toggle-selector :group.config/public-map-visible-licence
                   editor-ctx
                   (when (is-attr? :group.config/hide-from-public-map editor-ctx)
                     {:disabled?      true
                      :disabled-title (tr [:group-admin/public-map-visible-licence-hidden-help])})))

(defn bond-required-selector [{:keys [tr] :as editor-ctx} permit-type]
  (let [attr           :group.config/bond-required?
        checked?       (is-attr? attr editor-ctx)
        helper-text-tr (if (= permit-type :permit-type/CR)
                         [:group-admin/bond-required-on-submit-help]
                         [:group-admin/bond-required-help])]
    [:div.md:flex
     [toggle-selector attr editor-ctx]
     (when checked?
       [:div.md:ml-8.md:-mt-6.flex-1
        [ui/help (tr helper-text-tr)]])]))


(defn add-complainant-as-related-party-selector [{:keys [tr parent-config *group-config name-chain] :as editor-ctx}]
  (let [attr       :group.config/add-complainant-as-related-party?
        config-key (get-config-key attr parent-config)]
    [:div.mb-4
     (cc/checkbox
       {:id         (make-toggle-id attr name-chain)
        :label-text (tr [attr])
        :on-change  #(on-change attr *group-config)
        :checked    (is-attr? attr editor-ctx)
        :disabled   (boolean config-key)})
     (when config-key [ui/help (tr [:group-admin/disabled-reason] [config-key])])]))


(def pre-consultation-possible?
  (comp #{:group.config.pre-consultation/required
          :group.config.pre-consultation/optional}
        :group.config/pre-consultation))

(rf/reg-sub ::show-pre-consultation-selectors?
  (fn [[_ config-id]]
    (rf/subscribe [::entities/entity config-id]))
  (fn [{:group.config/keys [level] :as config} _]
    (or (and (= level :group-config.level/permit-type)
             (->> config :entity/_parent (some pre-consultation-possible?)))
        (and (= level :group-config.level/category)
             (pre-consultation-possible? config)))))

(rf/reg-sub ::phase-items
  (fn [_ [_ permit-type config-id]]
    (if (= permit-type :permit-type/PI)
      [:config-page/periodic-parent
       :config-page/periodic-child]
      (->> [(when (<sub [::show-pre-consultation-selectors? config-id])
              :config-page/pre-consultation)
            (when-not (contains? ptc-model/execution-only-types permit-type)
              :config-page/application)
            (when-not (contains? ptc-model/application-only-types permit-type)
              :config-page/execution)
            (when (and (not= permit-type :permit-type/CR)
                       ;; when no property attributes, do not show page
                       (seq (<sub [::conf-sel/attribute-templates])))
              :config-page/property-attribute)]
           (remove nil?)))))

(defn selectors [{:keys [tr permit-type target level] :as editor-ctx}]
  (let [uses-closure-categories?  (contains? ptc-model/permit-types-with-closure-categories permit-type)
        renewals?                 (contains? ptc-model/permit-types-with-renewal permit-type)
        app-inspections?          (<sub [::gas/has-feature? :group.feature/application-inspections])
        exec-inspections?         (<sub [::gas/has-feature? :group.feature/execution-inspections])
        ;; target and permit-type are such, that multiple configuration branches may apply to a workspace
        multi-select?             (and (contains? ptc-model/permit-types-with-multiple-work-types permit-type)
                                       (contains? config-model/multi-select-levels level))
        category-or-permit-type?  (or (= level :group-config.level/permit-type)
                                      (= level :group-config.level/category))
        show-public-map-hide?     (and (<sub [::gas/has-feature? :group.feature/public-map])
                                       (<sub [::gas/parent-has-feature? :group.feature/domain-public-map])
                                       category-or-permit-type?)
        show-pr-cr?               (and (= permit-type :permit-type/CR)
                                       (= level :group-config.level/permit-type)
                                       (<sub [::ff/enabled? :feature-flag.id/licence-and-contractor-registration-public-records])
                                       (<sub [::gas/parent-has-feature? :group.feature/domain-public-map]))
        show-pr-licence?          (and (= permit-type :permit-type/L)
                                       (#{:group-config.level/permit-type
                                          :group-config.level/category} level)
                                       (<sub [::ff/enabled? :feature-flag.id/licence-and-contractor-registration-public-records])
                                       (<sub [::gas/parent-has-feature? :group.feature/domain-public-map]))
        reviews-in-submitted?     (and (<sub [::ff/enabled? :feature-flag.id/reviews-in-submitted])
                                       (<sub [::gas/has-feature? :group.feature/reviews-in-submitted]))
        reason-conf?              (<sub [::ff/enabled? :feature-flag.id/inspection-visit-configuration])
        pre-issuance-inspections? (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
        editor-ctx                (assoc editor-ctx :reason-conf? reason-conf?)
        execution-only?           (contains? ptc-model/execution-only-types permit-type)]
    (r/with-let [*pc-or-app (r/atom
                              (cond
                                (= permit-type :permit-type/PI)
                                :config-page/periodic-parent
                                execution-only?
                                :config-page/execution
                                :else
                                :config-page/application))]
      (let [pc-or-app @*pc-or-app]
        [:<>
         [ui-cs/content-switcher {:items     (<sub [::phase-items permit-type target])
                                  :val->str  #(tr [%])
                                  :current   pc-or-app
                                  :on-change #(reset! *pc-or-app %)
                                  :class     "mb-4"}]
         (case pc-or-app
           :config-page/application
           [:<>
            [approval-authority-config/authority-select editor-ctx]
            [conf-sel/form-schema-editor editor-ctx (<sub [::cfs-subs/base-schemas])]
            (when-not multi-select?
              [conf-sof/sign-off-config editor-ctx])

            [conf-sel/attachments-editor editor-ctx]
            [section/subsection
             {:title (tr [:group-admin/party-roles-title])}
             [:<>
              [conf-sel/applicable-roles-editor editor-ctx]
              [conf-sel/req-roles-editor editor-ctx]
              [conf-sel/req-roles-issuance-editor editor-ctx]
              [conf-role-form/role-form-configurator editor-ctx]]]
            (if reviews-in-submitted?
              [:<>
               [conf-sel/review-config-editor editor-ctx
                :group.config/review-configs
                :group-admin/default-reviews-during-in-review-step]
               [conf-sel/submitted-review-config-editor editor-ctx]]
              [conf-sel/review-config-editor editor-ctx])
            (when (and (not multi-select?)
                       reviews-in-submitted?)
              [require-sign-off-after-changes-requested-selector editor-ctx])
            (when (not multi-select?)
              [no-review-change-request-selector editor-ctx])
            (when (and (contains? ptc-model/permit-types-without-mandatory-location permit-type)
                       (not multi-select?))
              [no-workspace-location editor-ctx])
            (when show-public-map-hide?
              [hide-from-public-map editor-ctx])
            (when show-pr-cr?
              [public-map-visible-cr-selector editor-ctx])
            (when show-pr-licence?
              [public-map-visible-licence-selector editor-ctx])
            (when renewals?
              [:<>
               [conf-sel/licence-renewal-day-limit editor-ctx]
               [licence-notification-config/editor editor-ctx]])
            (when (and (#{:permit-type/L :permit-type/CR} permit-type)
                       (ff/enabled? :feature-flag.id/grace-period?))
              [conf-sel/grace-period-config editor-ctx])
            [conf-sel/default-conditions-editor editor-ctx]
            [conf-sel/conditions-verdict-config editor-ctx]
            [conf-sel/general-conditions-editor editor-ctx]
            [conf-sof/permit-sign-off-config editor-ctx]
            [express-issuance-config/express-issuance-config editor-ctx]
            [target-time-config/target-time-editor editor-ctx]
            [condition-due-date-config/condition-due-date-config editor-ctx]
            [integration-editor/integration-targets editor-ctx]
            [integration.ecm/group-config-editor editor-ctx]
            (when (and app-inspections?
                       (or pre-issuance-inspections?
                           ;; Licensing always has pre-issuance inspections.
                           (= permit-type :permit-type/L)))
              [:<>
               [inspection-sel/app-inspection-template-selector editor-ctx]
               [conf-sel/inspection-result-detail-editor editor-ctx]
               [conf-sel/label-groups-editor editor-ctx]])
            (when (not multi-select?)
              [verdict-defaults/defaults-conf editor-ctx])
            (when-not multi-select?
              (if (ff/enabled? :feature-flag.id/licence-numbering-by-config-tree)
                [case-id-config/case-id-editor-config-tree-with-source-selector editor-ctx]
                [case-id-config/case-id-editor-config-tree editor-ctx]))
            [approval-authority-config/select-initial-groups editor-ctx]
            (when (<sub [::gas/has-feature? :group.feature/use-bond-accounts])
              [bond-required-selector editor-ctx permit-type])]

           :config-page/pre-consultation
           [:<>
            [conf-sel/pre-consultation-form-schema-editor editor-ctx]
            [conf-sel/pre-consultation-attachments-editor editor-ctx]
            [conf-sel/pre-consultation-req-roles-editor editor-ctx]
            (when (and pre-issuance-inspections?
                       app-inspections?)
              [:<>
               [inspection-sel/app-inspection-template-selector editor-ctx]
               [conf-sel/inspection-result-detail-editor editor-ctx]
               [conf-sel/label-groups-editor editor-ctx]])
            [pre-con-expiration-config/view editor-ctx]]

           :config-page/property-attribute
           [conf-sel/property-attr-editor editor-ctx]
           :config-page/execution
           (conj
             (if uses-closure-categories?
               [:<>
                [conf-sel/form-schema-editor editor-ctx (<sub [::cfs-subs/execution-schemas])]
                [conf-sel/applicable-roles-editor editor-ctx]
                (when (and (<sub [::ff/enabled? :feature-flag.id/complaint-submission-configuration])
                           (ptc-model/permit-types-with-complaints permit-type)
                           category-or-permit-type?)
                  [add-complainant-as-related-party-selector editor-ctx])
                (when exec-inspections?
                  [:<>
                   [inspection-sel/inspection-template-selector editor-ctx]
                   [conf-sel/inspection-result-detail-editor editor-ctx]
                   [conf-sel/label-groups-editor editor-ctx]
                   [conf-sel/closure-categories-editor editor-ctx]])
                (when (and (ff/enabled? :feature-flag.id/grace-period?)
                           (contains? ptc-model/permit-types-with-fees-and-payments permit-type))
                  [conf-sel/grace-period-config editor-ctx])
                (if (ff/enabled? :feature-flag.id/licence-numbering-by-config-tree)
                  [case-id-config/case-id-editor-config-tree-with-source-selector editor-ctx]
                  [case-id-config/case-id-editor-config-tree editor-ctx])]
               [:<>
                (when-not execution-only? [execution-selector editor-ctx])
                (when-not (is-attr? :group.config/omit-execution-phase? editor-ctx)
                  [:<>
                   [conf-sel/form-schema-editor editor-ctx (<sub [::cfs-subs/execution-schemas])]
                   [conf-sel/applicable-roles-editor editor-ctx]
                   (when-not execution-only?
                     [conf-notification/notification-config editor-ctx])
                   (when exec-inspections?
                     [:<>
                      [inspection-sel/inspection-template-selector editor-ctx]
                      [conf-sel/inspection-result-detail-editor editor-ctx]
                      [conf-sel/label-groups-editor editor-ctx]])
                   [conf-sel/supplemental-permit-editor editor-ctx]])])
             (when execution-only?
               [condition-due-date-config/condition-due-date-config editor-ctx])
             (when execution-only?
               [integration.ecm/group-config-editor editor-ctx])
             (when show-public-map-hide?
               [hide-from-public-map editor-ctx]))
           :config-page/periodic-child
           [:<>
            [conf-sel/form-schema-editor editor-ctx (<sub [::cfs-subs/execution-schemas])]
            [conf-sel/applicable-roles-editor editor-ctx]
            [inspection-sel/inspection-template-selector editor-ctx]
            [conf-sel/inspection-result-detail-editor editor-ctx]
            [conf-sel/label-groups-editor editor-ctx]]
           :config-page/periodic-parent
           [:<>
            [conf-sel/form-schema-editor editor-ctx (<sub [::cfs-subs/periodic-schemas])]
            [conf-sel/attachments-editor editor-ctx]
            [section/subsection
             {:title (tr [:group-admin/party-roles-title])}
             [:<>
              [conf-sel/applicable-roles-editor editor-ctx]]]
            (if (ff/enabled? :feature-flag.id/licence-numbering-by-config-tree)
              [case-id-config/case-id-editor-config-tree-with-source-selector editor-ctx]
              [case-id-config/case-id-editor-config-tree editor-ctx])])]))))
